import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { doc, getDoc } from 'firebase/firestore';
import { COL, formatMinor, type LoyaltyAccount, type LoyaltyLedgerEntry } from '@eprintsmart/shared';
import { db } from '../firebase';
import { getLoyaltyAccount, getLoyaltyLedger } from '../services/loyalty';

/**
 * One customer's Print Credits history.
 *
 * The ledger is the real record — the wallet balance is a running total of it,
 * so showing both makes a disputed balance auditable rather than something the
 * customer has to be told to trust. Entry types are spelled out for the same
 * reason: HOLD and RELEASE explain a balance that dipped and came back.
 */
const TYPE_LABEL: Record<string, string> = {
  EARN: 'Earned',
  REDEEM: 'Spent',
  HOLD: 'Reserved for an order',
  RELEASE: 'Returned — order not completed',
  EXPIRE: 'Expired',
  REVERSAL: 'Reversed after refund',
};

interface Contact { name?: string; email?: string; phone?: string }

export function CreditCustomer() {
  const { uid = '' } = useParams();
  const [account, setAccount] = useState<LoyaltyAccount | null>(null);
  const [ledger, setLedger] = useState<LoyaltyLedgerEntry[] | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, l, c] = await Promise.all([
          getLoyaltyAccount(uid),
          getLoyaltyLedger(uid),
          getDoc(doc(db, COL.crmContacts, uid)).then((s) => (s.exists() ? (s.data() as Contact) : null)).catch(() => null),
        ]);
        if (cancelled) return;
        setAccount(a);
        setLedger(l);
        setContact(c);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this wallet.');
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!ledger) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2}>
      <Button component={RouterLink} to="/loyalty/customers" startIcon={<ArrowBackIcon />} sx={{ alignSelf: 'flex-start' }}>
        Credit board
      </Button>

      <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap">
        <Typography variant="h1">{contact?.name || 'Customer'}</Typography>
        {account && <Chip size="small" label={account.tier} />}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {[contact?.email, contact?.phone].filter(Boolean).join(' · ') || uid}
      </Typography>

      {contact && (
        <Button component={RouterLink} to={`/crm/${uid}`} size="small" sx={{ alignSelf: 'flex-start' }}>
          Open CRM contact →
        </Button>
      )}

      {!account ? (
        <Alert severity="info">This customer has no wallet yet.</Alert>
      ) : (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Paper sx={{ px: 2, py: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">Balance</Typography>
            <Typography variant="h6">{formatMinor(account.balanceMinor ?? 0)}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">Lifetime earned</Typography>
            <Typography variant="h6">{formatMinor(account.lifetimeEarnedMinor ?? 0)}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">Lifetime spent</Typography>
            <Typography variant="h6">{formatMinor(account.lifetimeRedeemedMinor ?? 0)}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">12-month spend</Typography>
            <Typography variant="h6">{formatMinor(account.spend12mMinor ?? 0)}</Typography>
          </Paper>
        </Stack>
      )}

      <Divider />
      <Typography variant="h2" sx={{ fontSize: '1.1rem' }}>Credit history</Typography>

      {ledger.length === 0 ? (
        <Alert severity="info">No credit activity yet.</Alert>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>What</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Expires</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{e.createdAt ? new Date(e.createdAt).toLocaleString('en-IN') : '—'}</TableCell>
                  <TableCell>
                    {TYPE_LABEL[e.type] ?? e.type}
                    {e.note ? <Typography variant="caption" color="text.secondary" display="block">{e.note}</Typography> : null}
                  </TableCell>
                  <TableCell align="right">{formatMinor(e.amountMinor ?? 0)}</TableCell>
                  <TableCell align="right">
                    {e.remainingMinor != null ? formatMinor(e.remainingMinor) : '—'}
                  </TableCell>
                  <TableCell>
                    {e.orderId
                      ? <RouterLink to={`/orders/${e.orderId}`}>{e.orderId.slice(0, 8)}…</RouterLink>
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {e.expiresAt ? new Date(e.expiresAt).toLocaleDateString('en-IN') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
